(() => {
  const SECTION_CLASS = "lookbook-section";
  const RAMP_DURATION = 400;
  const DRAG_THRESHOLD = 8;
  const CLONE_ATTR = "data-lookbook-clone";
  const MOBILE_QUERY = "(max-width: 749px)";
  const MOBILE_SPEED_FACTOR = 0.6;
  const WHEEL_IDLE = 300;
  const WHEEL_LINE_HEIGHT = 16;
  const HIDDEN_CLASS = "lookbook-card--hidden";

  const smoothstep = (t) => t * t * (3 - 2 * t);
  const wrap = (value, size) => ((value % size) + size) % size;

  const instances = new WeakMap();

  class LookbookMarquee {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector(".lookbook__track");
      this.cards = this.track
        ? Array.from(this.track.querySelectorAll(":scope > .lookbook-card"))
        : [];

      this.offset = 0;
      this.cycle = 0;
      this.velocity = 0;
      this.rampT = 1;
      this.rampTarget = 1;
      this.lastFrame = 0;
      this.rafId = null;
      this.resizeTimer = null;
      this.observer = null;
      this.classObserver = null;
      this.resizeObserver = null;
      this.motionHold = null;
      this.held = false;
      this.modalOpen = false;
      this.wheeling = false;
      this.wheelTimer = null;
      this.dragging = false;
      this.dragMoved = false;
      this.dragStartX = 0;
      this.dragStartOffset = 0;
      this.suppressClick = false;

      this.speed = Number(root.dataset.speed) || 15;
      this.direction = root.dataset.direction === "ltr" ? -1 : 1;
      this.axis = document.documentElement.dir === "rtl" ? -1 : 1;
      this.pauseOnHover = root.dataset.pauseOnHover !== "false";
      this.autoplay =
        root.dataset.autoplay === "true" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.tickerMobile = root.dataset.tickerMobile !== "false";
      this.tickerDesktop = root.dataset.tickerDesktop !== "false";
      this.active = null;
    }

    start() {
      if (!this.track || this.cards.length < 2) return;

      this.bind();
      this.sync();
    }

    isActive() {
      return window.matchMedia(MOBILE_QUERY).matches
        ? this.tickerMobile
        : this.tickerDesktop;
    }

    // The grid layout shares this DOM, so leaving the ticker is a full teardown:
    // the clones and the transform would otherwise show up as grid cells.
    sync(remeasure = false) {
      const active = this.isActive();
      const changed = active !== this.active;
      this.active = active;

      if (!active) {
        if (changed) this.teardown();
        return;
      }

      if (changed || remeasure) {
        this.build();
        this.play();
      }
    }

    teardown() {
      this.stop();
      if (!this.track) return;

      this.track
        .querySelectorAll("[" + CLONE_ATTR + "]")
        .forEach((clone) => clone.remove());
      this.track.style.transform = "";
      this.offset = 0;
      this.cycle = 0;
    }

    destroy() {
      this.teardown();
      clearTimeout(this.resizeTimer);
      clearTimeout(this.wheelTimer);
      this.observer?.disconnect();
      this.observer = null;
      this.classObserver?.disconnect();
      this.classObserver = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.releasePointer();
      this.motionHold?.destroy();
      this.motionHold = null;
      this.held = false;

      this.track?.removeEventListener("pointerdown", this.onPointerDown);
      this.track?.removeEventListener("wheel", this.onWheel);
      this.track?.removeEventListener("click", this.onClick, true);
      this.track?.removeEventListener("dragstart", this.onDragStart);
    }

    bind() {
      if (this.autoplay) {
        this.motionHold = createMotionHold(this.root, {
          hoverTarget: this.pauseOnHover ? this.root : null,
          onChange: (held) => {
            this.held = held;
            this.updateRamp();
          },
          onFocusIn: (target) => this.revealFocused(target),
          onFocusOut: () => {
            this.root.scrollLeft = 0;
          },
        });
      }

      this.track.addEventListener("pointerdown", this.onPointerDown);
      this.track.addEventListener("wheel", this.onWheel, { passive: false });
      this.track.addEventListener("click", this.onClick, true);
      this.track.addEventListener("dragstart", this.onDragStart);

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.play();
          else this.stop();
        });
      });
      this.observer.observe(this.root);

      this.classObserver = new MutationObserver(() => {
        this.modalOpen = !!this.track.querySelector(".is-modal-open");
        this.updateRamp();
      });
      this.classObserver.observe(this.track, {
        attributes: true,
        attributeFilter: ["class"],
        subtree: true,
      });

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.root);
    }

    build() {
      this.track
        .querySelectorAll("[" + CLONE_ATTR + "]")
        .forEach((clone) => clone.remove());

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const setWidth =
        this.cards.reduce(
          (total, card) => total + card.getBoundingClientRect().width,
          0
        ) +
        gap * (this.cards.length - 1);
      if (setWidth <= 0) return;

      this.cycle = setWidth + gap;
      const rate = window.matchMedia(MOBILE_QUERY).matches
        ? this.speed * MOBILE_SPEED_FACTOR
        : this.speed;
      this.velocity = (setWidth / this.cards.length) * (rate / 60);

      const sets = Math.ceil((this.root.clientWidth + gap) / this.cycle) + 1;
      for (let i = 1; i < sets; i++) {
        this.cards.forEach((card) =>
          this.track.appendChild(this.cloneCard(card))
        );
      }

      this.offset = wrap(this.offset, this.cycle);
      this.render();

      if (typeof initCursorFollow === "function") {
        this.track
          .querySelectorAll("[data-cursor-follow]")
          .forEach(initCursorFollow);
      }
    }

    cloneCard(card) {
      const clone = card.cloneNode(true);
      clone.setAttribute(CLONE_ATTR, "");
      clone.setAttribute("aria-hidden", "true");
      clone
        .querySelectorAll("a, button, [tabindex]")
        .forEach((node) => node.setAttribute("tabindex", "-1"));
      return clone;
    }

    render() {
      const shift = -wrap(this.offset, this.cycle) * this.axis;
      const ratio = window.devicePixelRatio || 1;
      const snapped = Math.round(shift * ratio) / ratio;
      this.track.style.transform = `translate3d(${snapped}px, 0, 0)`;
    }

    play() {
      if (!this.autoplay || this.rafId !== null || !this.cycle) return;
      this.lastFrame = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }

    stop() {
      if (this.rafId === null) return;
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    tick = (now) => {
      const delta = this.lastFrame ? Math.min(now - this.lastFrame, 64) : 0;
      this.lastFrame = now;

      const step = delta / RAMP_DURATION;
      if (this.rampT < this.rampTarget) {
        this.rampT = Math.min(this.rampTarget, this.rampT + step);
      } else if (this.rampT > this.rampTarget) {
        this.rampT = Math.max(this.rampTarget, this.rampT - step);
      }

      const rate = smoothstep(this.rampT);
      if (rate > 0) {
        if (!this.dragging) {
          this.offset += this.velocity * this.direction * rate * (delta / 1000);
          this.render();
        }
      } else if (this.rampTarget === 0) {
        this.rafId = null;
        return;
      }

      this.rafId = requestAnimationFrame(this.tick);
    };

    updateRamp() {
      this.rampTarget = this.held || this.modalOpen || this.wheeling ? 0 : 1;
      this.play();
    }

    // Keyboard focus stops the row at once and brings the focused card fully
    // into view. Only original cards take focus (clones are tabindex -1), and
    // the browser's own focus scroll of this overflow box is undone first.
    revealFocused(target) {
      this.rampT = 0;
      this.root.scrollLeft = 0;
      if (!this.active || !this.cycle) return;

      const card = target.closest(".lookbook-card");
      if (!card || !this.track.contains(card)) return;

      const track = this.track.getBoundingClientRect();
      const box = card.getBoundingClientRect();
      const start =
        this.axis === 1 ? box.left - track.left : track.right - box.right;
      const current = wrap(this.offset, this.cycle);
      const next = Math.min(
        start,
        Math.max(current, start + box.width - this.root.clientWidth)
      );
      if (next === current) return;

      this.offset = next;
      this.render();
    }

    wheelDelta(event) {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return 0;
      if (event.deltaMode === 1) return event.deltaX * WHEEL_LINE_HEIGHT;
      if (event.deltaMode === 2) return event.deltaX * this.root.clientWidth;

      return event.deltaX;
    }

    onWheel = (event) => {
      if (!this.cycle || this.dragging) return;

      const delta = this.wheelDelta(event);
      if (!delta) return;

      event.preventDefault();
      this.offset += delta * this.axis;
      this.render();

      this.wheeling = true;
      this.updateRamp();

      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => {
        this.wheeling = false;
        this.updateRamp();
      }, WHEEL_IDLE);
    };

    onPointerDown = (event) => {
      if (!this.cycle) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      this.dragging = true;
      this.dragMoved = false;
      this.suppressClick = false;
      this.dragStartX = event.clientX;
      this.dragStartOffset = this.offset;
      this.root.classList.add("is-dragging");

      window.addEventListener("pointermove", this.onPointerMove);
      window.addEventListener("pointerup", this.onPointerUp);
      window.addEventListener("pointercancel", this.onPointerUp);
    };

    onPointerMove = (event) => {
      const distance = event.clientX - this.dragStartX;
      if (!this.dragMoved && Math.abs(distance) < DRAG_THRESHOLD) return;

      this.dragMoved = true;
      this.offset = this.dragStartOffset - distance * this.axis;
      this.render();
    };

    onPointerUp = () => {
      if (!this.dragging) return;

      this.dragging = false;
      this.suppressClick = this.dragMoved;
      this.root.classList.remove("is-dragging");
      this.releasePointer();
      this.play();
    };

    releasePointer() {
      window.removeEventListener("pointermove", this.onPointerMove);
      window.removeEventListener("pointerup", this.onPointerUp);
      window.removeEventListener("pointercancel", this.onPointerUp);
    }

    onClick = (event) => {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    onDragStart = (event) => event.preventDefault();

    onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (!this.track.isConnected) return;
        this.sync(true);
      }, 250);
    };
  }

  class LookbookModalClose {
    constructor(modal) {
      this.modal = modal;
      this.content = modal.querySelector(".lookbook-modal__content");
      this.media = modal.querySelector(".lookbook-modal__media");
      this.close = modal.querySelector(".lookbook-modal__close");
      this.observer = null;
    }

    start() {
      if (!this.content || !this.media || !this.close) return;

      this.modal.addEventListener("scroll", this.update, {
        capture: true,
        passive: true,
      });
      window.addEventListener("resize", this.update);

      this.observer = new MutationObserver(this.update);
      this.observer.observe(this.modal, {
        attributes: true,
        attributeFilter: ["open"],
      });

      this.update();
    }

    destroy() {
      this.modal.removeEventListener("scroll", this.update, { capture: true });
      window.removeEventListener("resize", this.update);
      this.observer?.disconnect();
      this.observer = null;
      this.close?.classList.remove("is-over-media");
    }

    update = () => {
      const button = this.close.getBoundingClientRect();
      const media = this.media.getBoundingClientRect();
      const x = button.left + button.width / 2;
      const y = button.top + button.height / 2;
      const overMedia =
        x > media.left && x < media.right && y > media.top && y < media.bottom;

      this.close.classList.toggle("is-over-media", overMedia);
    };
  }

  class LookbookFilters {
    constructor(bar, section) {
      this.bar = bar;
      this.section = section;
      this.cards = Array.from(section.querySelectorAll(".lookbook-card"));
    }

    start() {
      this.bar.addEventListener("click", this.onClick);
    }

    destroy() {
      this.bar.removeEventListener("click", this.onClick);
      this.cards.forEach((card) => card.classList.remove(HIDDEN_CLASS));
    }

    onClick = (event) => {
      const tab = event.target.closest("[data-lookbook-filter]");
      if (!tab || !this.bar.contains(tab)) return;

      const value = tab.dataset.lookbookFilter;

      this.bar
        .querySelectorAll("[data-lookbook-filter]")
        .forEach((item) => item.classList.toggle("is-active", item === tab));

      this.cards.forEach((card) => {
        const matches = !value || card.dataset.lookbookFilter === value;
        card.classList.toggle(HIDDEN_CLASS, !matches);
      });
    };
  }

  const sectionModals = (section) =>
    Array.from(section.querySelectorAll("modal-opener[data-modal]"))
      .map((opener) => document.querySelector(opener.dataset.modal))
      .filter(Boolean);

  const boundSections = new WeakSet();

  const onSectionClick = (event) => {
    if (event.target.closest("a[href]")) return;
    if (event.target.closest(".lookbook-card__opener")) return;

    const card = event.target.closest(".lookbook-card");
    if (!card) return;

    card.querySelector(".lookbook-card__opener")?.click();
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;

    if (!boundSections.has(section)) {
      section.addEventListener("click", onSectionClick);
      boundSections.add(section);
    }

    section.querySelectorAll(".js-lookbook-marquee").forEach((root) => {
      instances.get(root)?.destroy();

      const marquee = new LookbookMarquee(root);
      instances.set(root, marquee);
      marquee.start();
    });

    section.querySelectorAll(".js-lookbook-filters").forEach((bar) => {
      instances.get(bar)?.destroy();

      const filters = new LookbookFilters(bar, section);
      instances.set(bar, filters);
      filters.start();
    });

    sectionModals(section).forEach((modal) => {
      instances.get(modal)?.destroy();

      const modalClose = new LookbookModalClose(modal);
      instances.set(modal, modalClose);
      modalClose.start();
    });
  };

  const destroySection = (section) => {
    if (!section) return;

    section.removeEventListener("click", onSectionClick);
    boundSections.delete(section);

    section.querySelectorAll(".js-lookbook-marquee").forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });

    section.querySelectorAll(".js-lookbook-filters").forEach((bar) => {
      instances.get(bar)?.destroy();
      instances.delete(bar);
    });

    sectionModals(section).forEach((modal) => {
      instances.get(modal)?.destroy();
      instances.delete(modal);
      if (!modal.hasAttribute("open")) modal.remove();
    });
  };

  initSection(document.currentScript.parentElement);

  if (!window.__themeLookbookEvents) {
    window.__themeLookbookEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      destroySection(event.target);
    });
  }
})();
